import { useState } from 'react';
import { ExpandMore as ExpandMoreIcon, ImageOutlined } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Link,
  Paper,
  Skeleton,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import { useNavigation, useNotification } from '@refinedev/core';
import { useForm } from '@refinedev/react-hook-form';
import { AxiosError } from 'axios';
import { Controller, FormProvider } from 'react-hook-form';

import { AiRemirror } from '@attackiq/components/AiRemirror';
import { Dropzone } from '@attackiq/components/Dropzone';

import { NoRefreshEdit } from '../../components';
import ContentModules from '../../components/ContentModules';
import TabPanel from '../../components/mui/TabPanel/TabPanel';
import { uploadFile } from '../../utils/uploadFile';

import { BinaryNameDialog } from './components/binary_name_dialog';
import { buildPackage } from './components/build_package';
import { Status } from './components/constants';
import {
  CreditsInput,
  FeaturedCheckbox,
  NameInput,
  PackageArchSelector,
  PackageOSSelector,
  PackageTypeInput,
  PurposeInput,
  VersionInput
} from './components/input_components';

const FlexPackageTabs = {
  General: 0,
  Modules: 1
} as const;

type FlexPackageTabs = (typeof FlexPackageTabs)[keyof typeof FlexPackageTabs];

const FlexPackagesEdit = () => {
  const [tab, setTab] = useState<FlexPackageTabs>(FlexPackageTabs.General);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | undefined | null>(null);
  const { saveButtonProps, ...formMethods } = useForm({
    refineCoreProps: {
      action: 'edit',
      queryOptions: {
        staleTime: 0,
        gcTime: 0,
        structuralSharing: false
      }
    }
  });
  const {
    refineCore: { formLoading, onFinish, id, query: queryResult },
    control,
    handleSubmit
  } = formMethods;

  const { editUrl } = useNavigation();
  const { open } = useNotification();

  const onSubmit = handleSubmit(
    async ({ name, description, package_type, version, credits, featured, purpose, os, arch }) => {
      if (selectedFile) {
        try {
          await uploadFile(selectedFile, `/v2/flex_packages/${id}/upload_package_image`, 'image', 'put');
        } catch (error) {
          const message = (error as AxiosError<{ detail: string }>).response?.data?.detail;

          if (message) {
            open?.({ message, type: 'error' });
          }
        }
      }
      onFinish({
        name: name,
        description: description ? description : '',
        package_type: package_type.id,
        version: version,
        credits: credits,
        featured: featured,
        purpose: purpose,
        os: os,
        arch: arch
      });
    }
  );

  const currentStatus = queryResult?.data?.data?.status;
  const buildStatus = currentStatus ?? Status.IN_PROGRESS;

  const handleBuildPackage = async () => {
    if (buildStatus !== Status.IN_PROGRESS) {
      setIsDialogOpen(true);
    }
  };

  const handleCloseDialog = async (success: boolean, binary_name: string) => {
    setIsDialogOpen(false);

    if (success && id) {
      buildPackage({
        id,
        binaryName: binary_name,
        onFinish: () => {
          queryResult?.refetch();
        },
        onError: e => {
          open?.({ type: 'error', message: 'Error calling create_package_file' });
        }
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2
        }}
      >
        <Paper variant="outlined">
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="subtitle2">Flex Package ID: {queryResult?.data?.data.id}</Typography>
              <Typography variant="subtitle2">
                Assessment Template:{' '}
                <Link href={editUrl('assessment_templates', queryResult?.data?.data.project_template)}>
                  {queryResult?.data?.data.project_template}
                </Link>
                <br />
              </Typography>
              <Typography variant="subtitle2">Status: {buildStatus}</Typography>
              <Typography variant="subtitle2">Message: {queryResult?.data?.data.message}</Typography>
              <Typography variant="subtitle2">
                File: <Link href={queryResult?.data?.data.package_file}>Link</Link>
              </Typography>
            </Box>
            <Box>
              <Button
                variant="contained"
                disabled={buildStatus === Status.IN_PROGRESS}
                onClick={() => handleBuildPackage()}
              >
                Rebuild this package {buildStatus === Status.IN_PROGRESS ? '(Refresh for updates)' : ''}
              </Button>
            </Box>
          </Box>
        </Paper>
        <FormProvider {...formMethods}>
          <form name="create-flex-packages-form" onSubmit={onSubmit}>
            <NoRefreshEdit
              canDelete
              deleteButtonProps={{
                confirmTitle: 'Do you want to delete Flex Package?'
              }}
              isLoading={formLoading}
              saveButtonProps={{ type: 'submit', disabled: tab === FlexPackageTabs.Modules }}
            >
              {formLoading ? (
                <Skeleton variant="rectangular" height={500} width="100%" />
              ) : (
                <>
                  <NameInput required={false} />
                  <br />
                  <Paper square variant="outlined">
                    <Tabs value={tab} onChange={(_event, value) => setTab(value)} aria-label="Flex Package sections">
                      <Tab label="General" />
                      <Tab label="Modules" />
                    </Tabs>

                    <TabPanel value={tab} index={FlexPackageTabs.General}>
                      <Accordion defaultExpanded>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>Flex Package description*</AccordionSummary>
                        <AccordionDetails>
                          <Controller
                            control={control}
                            name="description"
                            rules={{ required: 'Flex Package description is required' }}
                            render={({ field: { onChange, value } }) => {
                              if (value === undefined) {
                                return <>Loading...</>;
                              }
                              return <AiRemirror value={value || ''} onChange={onChange} />;
                            }}
                          />
                        </AccordionDetails>
                      </Accordion>

                      <PackageTypeInput />
                      <VersionInput />
                      <CreditsInput />
                      <FeaturedCheckbox defaultValue={queryResult?.data?.data.featured} />
                      <PurposeInput />
                      <PackageOSSelector defaultValue={queryResult?.data?.data.os} />
                      <PackageArchSelector defaultValue={queryResult?.data?.data.arch} />
                      <br />
                      <Dropzone
                        rules={{
                          accept: 'image/*'
                        }}
                        onDropAccepted={files => {
                          setSelectedFile(files[0]);
                        }}
                        width="100%"
                        height={152}
                      >
                        <Dropzone.Body>
                          <>
                            <Typography>
                              Package Image: <Link component="span">Click to upload</Link> or drag and drop
                            </Typography>
                            <Typography color="text.secondary">Images supported only</Typography>
                          </>
                        </Dropzone.Body>

                        <Dropzone.Cover
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexDirection="column"
                          bgcolor="background.paper"
                          in={!!selectedFile}
                        >
                          <ImageOutlined color="primary" />
                          <Typography>{selectedFile?.name}</Typography>
                        </Dropzone.Cover>
                      </Dropzone>
                    </TabPanel>

                    <TabPanel value={tab} index={FlexPackageTabs.Modules}>
                      <ContentModules contentType="flexpackage" />
                    </TabPanel>
                  </Paper>
                </>
              )}
            </NoRefreshEdit>
          </form>
        </FormProvider>
      </Box>
      <BinaryNameDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        binaryName={queryResult?.data?.data.binary_name}
      />
    </>
  );
};

export default FlexPackagesEdit;
